// Page titles, subtitles and back targets for the RC Admin layout. Split out of
// Layout.jsx so that file exports only components and keeps fast refresh.

const PAGE_TITLES = {
  "/dashboard":           "Dashboard",
  "/rc/listings":         "Manage Listings",
  "/listings/lookbooks":  "Lookbooks",
  "/lookbooks":           "Edit Lookbook",
  // Purpose-built flows must precede the bare "/product/create" prefix —
  // getPageTitle matches on startsWith in insertion order.
  "/product/create/stock":       "New Stock",
  "/product/create/consignment": "New Consignment",
  "/product/create":      "Create Listing",
  "/requests/lookbooks":  "Lookbooks",
  "/requests/orders":     "Orders",
  "/requests/merchant":   "Merchant Orders",
  "/orders":              "Cart Orders",
  "/users":               "User CRM",
  "/profile":             "My Profile",
  // Leads tabs are listed before the bare "/leads" fallback — getPageTitle
  // matches on startsWith and returns the first hit, so the specific paths
  // must come first.
  "/leads/in-stock":      "In Stock",
  "/leads/in-consignment":"In Consignment",
  "/leads/leads":         "All Leads",
  "/leads/alerts":        "Buy Alerts",
  "/leads":               "Leads",
  "/kanban":              "Kanban",
  // Purpose-built request flows — listed before the shorter "/request/..."
  // prefixes they would otherwise be swallowed by (startsWith, first hit wins).
  "/request/record-sale": "Record a Sale",
  "/request/sourcing":    "Sourcing Enquiry",
  "/request/sell-enquiry":"Sell Enquiry",
  "/request/sell":        "Sell Request",
  "/request/order":       "Order Request",
  "/request/px-order":    "Part Exchange",
  "/request/px-cart":     "Part Exchange",
  "/request":             "New Request",
};

const PAGE_SUBTITLES = {
  "/rc/listings":
    "View, edit, and organize all your active and archived listings in one place.",
  "/kanban":
    "Track order and sell requests by stage, from new enquiry through to completion.",
  // Figma node 421-5630 (.Leads Page). Confirmed against staging review feedback
  // (Leads, Opportunities - Observations): subtitle reads "Leads that match your
  // inventory (potential sale)."
  "/leads/in-stock":
    "Leads that match your inventory (potential sale).",
  // In Consignment and All Leads have no subtitle in any frame seen so far —
  // left absent rather than invented. getPageSubtitle returns null and the
  // Topbar simply omits the line.
  //
  // Request flows (M54G). Ordered longest-prefix first: `getPageSubtitle` returns
  // the first `startsWith` hit, so "/request/sell-enquiry" has to precede
  // "/request/sell", and the bare "/request" comes last.
  "/request/sell-enquiry":
    "Take a watch in from a client — buy it outright or on consignment.",
  "/request/record-sale":
    "Log a sale you have already agreed, against a listing in your stock.",
  "/request/sourcing":
    "Find a watch for a client, quote it, and take the deal through to payment.",
  "/request/px-order":
    "Part-exchange: the watch coming in and the watch going out, in one deal.",
  "/request/px-cart":
    "Part-exchange: the watch coming in and the watch going out, in one deal.",
  "/request/order":
    "Source a watch for a client and quote it.",
  "/request/sell":
    "Buy a watch from a client, or take it on consignment.",
  "/request":
    "Start a sourcing or a sell enquiry and carry it through to a quote.",
};

const getPageTitle = (pathname) => {
  if (pathname.includes("/edit/")) return "Edit Listing";
  for (const [prefix, title] of Object.entries(PAGE_TITLES)) {
    if (pathname.startsWith(prefix)) return title;
  }
  return null;
};

const getPageSubtitle = (pathname) => {
  for (const [prefix, subtitle] of Object.entries(PAGE_SUBTITLES)) {
    if (pathname.startsWith(prefix)) return subtitle;
  }
  return null;
};

// Resolve where the back arrow should go for a given page. Returns an explicit
// destination for known flows, or null to fall back to history.goBack().
//   - Sell / Order / PX-order / New request flows ("/request/...") → Orders list
//   - Edit Listing flow ("/.../edit/...")                          → Manage Listings
const getBackTarget = (pathname) => {
  if (pathname.startsWith("/request/")) return "/requests/orders";
  if (pathname.includes("/edit/")) return "/rc/listings";
  return null;
};

export { getPageTitle, getPageSubtitle, getBackTarget };
